import EPageHeader from '@/components/everridge/EPageHeader';
import { useTranslation } from 'react-i18next';
import OrdersPageColumns from '@/features/orders/components/OrdersPageColumns';
import { useState } from 'react';
import OrderDetails from '@/features/orders/components/OrderDetails';
import NewOrder from '@/features/orders/components/NewOrder/NewOrder';
import { Button } from '@/components/ui/button';
import PackageAddIcon from '@/components/icons/PackageAddIcon';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import { useOrdersListApi } from '@/features/orders/hooks/useOrdersListApi';
import { useOrdersFilter } from '@/features/orders/hooks/useOrdersFilter';
import FilterIcon from '@/components/icons/FilterIcon';
import OrdersPageFilter from '@/features/orders/components/OrdersPageFilter';
import { OrderStatus } from '@/features/orders/types/orders-dto';
import { FilterHelper } from '@/utils/filterHelper';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { usePermissionsListApi } from '@/features/settings/hooks/usePermissionsListApi';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import OrdersTableMobile from '@/features/orders/components/OrdersInfo/OrdersTableMobile';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';
import { Navigate } from 'react-router-dom';

const OrdersPage = () => {
	const [orderId, setOrderId] = useState('');
	const [page, setPage] = useState(0);
	const [showOrdersFilter, setShowOrdersFilter] = useState(false);
	const [showOrderDetails, setShowOrderDetails] = useState(false);
	const [showNewOrder, setShowNewOrder] = useState(false);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();
	const columns = OrdersPageColumns();
	const userRole = useUserRole();
	const { permissionsListData: permissions } = usePermissionsListApi({
		role: userRole,
	});
	const { ordersEnabled } = useMultiCurrencyStorage();
	const canPlaceOrders = ordersEnabled && !!permissions?.placeOrders;

	const { ordersListData, isLoadingOrdersListData } = useOrdersListApi({
		page,
	});

	const { filter, setFilter } = useOrdersFilter();

	if (!ordersEnabled) {
		return <Navigate to='/' replace />;
	}
	const handlePageChange = (newPage: number) => {
		setPage(newPage);
		setFilter({
			skip: filter.take * newPage,
		});
	};

	const handleShowOrder = (id: string, status: OrderStatus) => {
		setOrderId(id);

		if (status === OrderStatus.Draft) {
			setShowNewOrder(true);
		} else {
			setShowOrderDetails(true);
		}
	};

	const handleCloseOrderDetails = () => {
		setOrderId('');
		setShowOrderDetails(false);
	};

	const handleCloseNewOrder = () => {
		setOrderId('');
		setShowNewOrder(false);
	};

	const filtersQuantity = FilterHelper.countFilterValues(filter, [
		'orderBy',
		'orderDirection',
		'skip',
		'take',
	]);
	
	return (
		<section className={'h-full flex flex-col gap-6'}>
			<EPageHeader title={t('main.orders.title')}>
				<div className='relative'>
					{!!filtersQuantity && (
						<div className='absolute -top-1 right-0 size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10 animate-pulse '>
							{filtersQuantity}
						</div>
					)}
					<Button
						variant='outline'
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowOrdersFilter(true)}>
						<FilterIcon />
						{!isMobile && t('elements.buttons.filter')}
					</Button>
				</div>
				{canPlaceOrders && (
					<Button
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowNewOrder(true)}>
						<PackageAddIcon className='stroke-popover-10' />
						{!isMobile && t('elements.buttons.newOrder')}
					</Button>
				)}
			</EPageHeader>
			{!isMobile ? (
				<ETableWithApiPagination
					columns={columns}
					data={ordersListData}
					page={page}
					setPage={handlePageChange}
					showDetails={handleShowOrder}
				/>
			) : (
				<OrdersTableMobile
					data={ordersListData}
					isLoading={isLoadingOrdersListData}
					page={page}
					setPage={handlePageChange}
					showDetails={handleShowOrder}
				/>
			)}
			{showOrderDetails && (
				<OrderDetails
					id={orderId}
					onClose={handleCloseOrderDetails}
					isMobile={isMobile}
				/>
			)}
			{showNewOrder && (
				<NewOrder
					id={orderId}
					onClose={handleCloseNewOrder}
				/>
			)}
			{showOrdersFilter && (
				<OrdersPageFilter onClose={() => setShowOrdersFilter(false)} />
			)}
		</section>
	);
};

export default OrdersPage;
